<template>
  <div
    ref     = "container"
    class   = "waveform-container"
  >
    <canvas
      ref    = "canvas"
      class  = "waveform-canvas"
    >
    </canvas>

    <div
      v-if  = "hideMetadata"
      class = "metadata-tooltip"
    >
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            class  = "mr-1 mb-1"
            v-on   = "on"
            v-bind = "attrs"
          >
            mdi-information-outline
          </v-icon>
        </template>
        Some details hidden at current zoom level.
      </v-tooltip>
    </div>

  </div>
</template>

<script lang = "js">

// #region Imports

/* vuex */
import {mapState} from 'vuex';

/* Telestream */
import {WAVEFORM_ELEMENTS} from '@/utils/constants';
import {collapseCues, filterCues, getAudioMetersTrack, getMetadataTrack} from './utilities/cue-utilities';
import {clearCanvas, renderMetadata, renderNielsenFlags, renderWaveform} from './utilities/render-utilities';
import {getMetadataFlags, getNielsenFlags, isInsideRectangle} from "@/utils/waveform-utilities";
import {JUMP_TO_TIME} from "@/utils/action-types";
import {SET_IS_WAVEFORM_FLAG_CLICKED} from "@/utils/mutation-types";
import {searchForCue} from "@user-interface/glim-player/lib/services/metadata.service";

// #endregion Imports

// #region Constants

const CANVAS_HEIGHT                   = 200;

const RENDER_INTERVAL_IN_MS           = 250;
const METADATA_RENDERING_CUTOFF_IN_S  = 15;
const WAVEFORM_BAR_WIDTH_IN_PX        = 4;
const ALIGN_CUES_THRESHOLD            = 3;

// #endregion Constants

export default {

  // #region Properties

  name:  'WaveformCanvas',
  props: {
    start: Number,
    end:   Number,
  },
  data() {
    return {
      context:                undefined,
      frameMetadataMaxLength: 1,

      interval:     undefined,
      sizeObserver: undefined,
      renderIPB:    false,
      metadataCues: null
    }
  },
  computed: {
    ...mapState(['video', 'videoController']),
    videoCurrentTime() {
      return this.$store.state.video.currentTime;
    },
    videoElement() {
      return this.$store.state.video.element;
    },
    selectedWaveformMarks() {
      return this.$store.state.video.selectedWaveformMarks;
    },
    currentAudioTrackIndex() {
      return this.videoController?.currentAudioTrack?.track;
    },
    hideMetadata() {
      return (this.end - this.start) > METADATA_RENDERING_CUTOFF_IN_S;
    }
  },
  watch: {
    start() {
      this.render();
    }
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.context = this.$refs.canvas.getContext('2d');

    // To keep rendering crisp:
    this.$refs.canvas.height = CANVAS_HEIGHT;
    this.$refs.canvas.width = this.$refs.container.clientWidth;
    this.sizeObserver = new ResizeObserver(this.onContainerResized).observe(this.$refs.container);

    this.interval = setInterval(() => this.render(), RENDER_INTERVAL_IN_MS);
    this.handleWaveformFlagClick();
  },

  destroyed() {
    if (this.sizeObserver) {
      this.sizeObserver.unobserve(this.$refs.container);
    }

    if (this.interval) {
      clearInterval(this.interval);
    }
  },

  methods: {

    // #region Events

    onContainerResized() {
      if (!this.$refs.container) {
        return;
      }

      this.$refs.canvas.width = this.$refs.container.clientWidth;
      this.$refs.canvas.height = this.$refs.container.clientHeight;
      this.render();
    },

    // #endregion Events

    render() {
      clearCanvas(this.context);
      this.renderWaveform();
      this.renderMetadata();
    },

    renderWaveform() {
      const track = getAudioMetersTrack(this.video.element, this.currentAudioTrackIndex);


      if (this.selectedWaveformMarks.includes(WAVEFORM_ELEMENTS.WAVEFORM) && this.metadataCues) {
        const interval = Math.max(
          1 / this.video.framerate,
          (this.end - this.start) * ((WAVEFORM_BAR_WIDTH_IN_PX + 1) / this.context.canvas.width)
        );
        // TODO when backend aligns cues
        // align cues accordingly to metadata cues - there is discrepancy between startTime and endTime audio vs metadata cues
        let cues;
        if (this.end - this.start > ALIGN_CUES_THRESHOLD) {
          cues = collapseCues(track, this.start, this.end, interval);
        } else {
          //small offset for proper cue display to ensure that edge cues are displayed properly on the timeline
          cues = filterCues(track, this.start - 1 > 0 ? this.start - 1 : 0, this.end + 1);
          this.adjustAudioMeterCuesTimeByMetadataCues(cues);

        }
        renderWaveform(this.context, cues, this.start, this.end);
      }

      if (
        this.selectedWaveformMarks.includes(WAVEFORM_ELEMENTS.NIELSEN) && this.metadataCues
      ) {
        const cues = filterCues(track, this.start, this.end);
        renderNielsenFlags(this.context, cues, this.start, this.end, this.metadataCues);
      }

    },

    adjustAudioMeterCuesTimeByMetadataCues: function (cues) {
      for (let i = 0; i < cues.length; i++) {
        let relatedMetadataCue = searchForCue(this.metadataCues, cues[i].startTime);
        if (relatedMetadataCue) {
          cues[i].startTime = relatedMetadataCue.startTime;
          cues[i].endTime = relatedMetadataCue.endTime;
        }
      }
    },

    renderMetadata() {
      const track = getMetadataTrack(this.video.element);
      const cues = filterCues(track, this.start, this.end);
      if (cues.length === 0) {
        return;
      }
      //small offset for proper cue display to ensure that edge cues are displayed properly on the timeline
      this.metadataCues = filterCues(track, this.start - 1 > 0 ? this.start - 1 : 0, this.end + 1);
      this.frameMetadataMaxLength = this.calculateFrameMetadataMaxLength(cues, this.frameMetadataMaxLength);
      renderMetadata(this.context, cues, this.start, this.end, this.frameMetadataMaxLength, this.selectedWaveformMarks, this.hideMetadata);
    },

    calculateFrameMetadataMaxLength(cues, frameMetadataMaxLength) {
      let maxLength = frameMetadataMaxLength;
      for (let cue of cues) {
        if (cue.value.info.sampleLength > maxLength) {
          maxLength = cue.value.info.sampleLength;
        }
      }
      return maxLength;
    },

    handleWaveformFlagClick(){
      this.$refs.canvas.addEventListener('mousedown', (event)=>{
        let waveformContainerData = document.getElementsByClassName('waveform-container')[0];
        const track = getAudioMetersTrack(this.video.element, this.currentAudioTrackIndex);

        const metadataTrack = getMetadataTrack(this.video.element);
        const metadataCues = filterCues(metadataTrack, this.start, this.end);
        let metadataFlags = getMetadataFlags(metadataCues, this.start, this.end, this.context)
        this.checkIfClickIsOnFlag(metadataFlags, event, waveformContainerData);

        const cues = filterCues(track, this.start, this.end);
        let nielsenFlags = getNielsenFlags(cues, this.start, this.end, this.context, metadataCues);
        this.checkIfClickIsOnFlag(nielsenFlags, event, waveformContainerData);
      })
    },

    checkIfClickIsOnFlag: function (metadataFlags, event, waveformContainerData) {
      metadataFlags.forEach(flag => {
        let x = event.offsetX / waveformContainerData.clientWidth;
        let y = event.offsetY / waveformContainerData.clientHeight;
        x = x * this.context.canvas.width;
        y = y * this.context.canvas.height;
        if (isInsideRectangle(flag.x, flag.y, flag.width, flag.height, x, y)) {
          event.preventDefault();
          event.stopPropagation();
          this.$store.dispatch(JUMP_TO_TIME, flag.position);
          this.$store.commit(SET_IS_WAVEFORM_FLAG_CLICKED, true);
        }
      })
    },
  }

  // #endregion Life-Cycle

};
</script>

<style scoped lang = "scss">

.waveform-container {
  position: relative;
  width:    100%;
  height:   100%;

  .waveform-canvas {
    position: absolute;
    top:      0px;
    right:    0px;
    width:    100%;
    height:   100%;
  }

  .metadata-tooltip {
    position: absolute;
    right:    0px;
    bottom:   0px;
  }
}

</style>
